import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Users, FilePlus, Send, X,Plus,Check,FileText, Clock,Tag,UserCheck,Upload, ChevronDown,ChevronUp,Search, Filter, Calendar,AlertCircle, Loader, CheckCircle,
  Mail,
  Bell,
  UserPlus,
} from "lucide-react";

const Compose = () => {
  const [subject, setSubject] = useState("");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [shortNotice, setShortNotice] = useState("");
  const societyName = localStorage.getItem("society");
  const [occasions, setOccasions] = useState([]);
  const [loadingOccasions, setLoadingOccasions] = useState(true);
  const [occasionError, setOccasionError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalField, setModalField] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleFileChange = (e) => {
    if (e && e.target && e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    } else {
      setFile(null);
    }
  };

  const getMemberKey = (member) => {
    return (
      member &&
      (member.unit_no ||
        member.wing ||
        member.member_name ||
        JSON.stringify(member))
    );
  };

  const toggleMemberSelection = (member) => {
    if (!member) return;
    const key = getMemberKey(member);
    setSelectedMembers((prev) => {
      const exists = prev.some((m) => getMemberKey(m) === key);
      if (exists) return prev.filter((m) => getMemberKey(m) !== key);
      return [...prev, member];
    });
  };

  const toggleSelectAll = () => {
    if (!Array.isArray(data) || data.length === 0) return;
    if (areAllMembersSelected()) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(data.map((m) => ({ ...m })));
    }
  };

  const areAllMembersSelected = () => {
    if (!Array.isArray(data) || data.length === 0) return false;
    return data.every((m) =>
      selectedMembers.some((s) => getMemberKey(s) === getMemberKey(m)),
    );
  };

  const areSomeMembersSelected = () => {
    if (!Array.isArray(data) || data.length === 0) return false;
    return selectedMembers.length > 0 && !areAllMembersSelected();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !shortNotice) {
      toast.error("Please fill in all required fields", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    const recipientText =
      selectedMembers.length > 0
        ? `${selectedMembers.length} selected member(s)`
        : "all members";

    const proceed = window.confirm(
      `Send notice "${subject}" to ${recipientText}?`,
    );
    if (!proceed) return;

    const token = localStorage.getItem("token");
    await submitFormDataToServer(token);
  };

  const submitFormDataToServer = async (token, extra = {}) => {
    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("shortNotice", shortNotice);
    formData.append("subject", subject);
    formData.append("title", title);
    formData.append("societyName", societyName);

    if (selectedMembers.length > 0) {
      const names = selectedMembers.map((m) => m.member_name || "").join(", ");
      formData.append("memberName", names);
      selectedMembers.forEach((m) =>
        formData.append("memberUnit", m.unit_no || ""),
      );
    } else {
      formData.append("memberName", "");
    }

    if (extra && extra.verificationTransactionId) {
      formData.append(
        "verificationTransactionId",
        extra.verificationTransactionId,
      );
    }

    try {
      const res = await fetch(`${BASE_URL}/api/compose/add-compose`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        const data = await res.text();
        toast.success("Notice sent successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setSubject("");
        setShortNotice("");
        setFile(null);
        setTitle("");
        setSelectedMembers([]);
      } else {
        const errorText = await res.text();
        toast.error("Failed to send notice: " + errorText, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("final submit error:", err);
      toast.error("Error submitting notice: " + (err.message || err), {
        position: "bottom-right",
        autoClose: 4000,
      });
    }
  };

  const fetchOccasions = async () => {
    const token = localStorage.getItem("token");
    const soc = localStorage.getItem("society");
    setLoadingOccasions(true);
    setOccasionError("");

    try {
      const url = `${BASE_URL}/occasion/get-occasion?societyName=${encodeURIComponent(soc || "")}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Error ${res.status}`);
      }

      const data = await res.json().catch(() => []);
      if (Array.isArray(data)) {
        setOccasions(data);
      } else if (data && data.data && Array.isArray(data.data)) {
        setOccasions(data.data);
      } else {
        setOccasions([]);
      }
    } catch (err) {
      console.error("Failed to load occasions:", err);
      toast.error("Failed to load occasions", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setOccasionError(err.message || "Failed to load occasions");
      setOccasions([]);
    } finally {
      setLoadingOccasions(false);
    }
  };

  useEffect(() => {
    fetchOccasions();
  }, []);

  const handleModalSubmit = async (e) => {
    e.preventDefault();

    if (!modalField || !modalField.trim()) {
      toast.error("Please enter a value", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const formData = new FormData();
      formData.append("occasion", modalField.trim());
      formData.append("societyName", societyName);

      const res = await fetch(`${BASE_URL}/occasion/add-occasion`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        const data = await res.text();
        toast.success("Occasion added successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setModalField("");
        setShowModal(false);
        await fetchOccasions();
      } else {
        const errorText = await res.text();
        toast.error("Failed to add occasion: " + errorText, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("Error: " + err.message, {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const society = localStorage.getItem("society");

      try {
        if (!token) {
          setError("Not logged in");
          setData([]);
          setLoading(false);
          return;
        }
        if (!society) {
          setError("Society not set");
          setData([]);
          setLoading(false);
          return;
        }

        const resp = await axios.get(`${BASE_URL}/api/member/get-list`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { society },
          validateStatus: null,
        });

        if (cancelled) return;

        if (resp.status === 200) {
          const payload = resp.data;
          if (payload == null) {
            setData([]);
            setError(null);
          } else if (Array.isArray(payload)) {
            setData(payload);
            setError(null);
          } else if (Array.isArray(payload.data)) {
            setData(payload.data);
            setError(null);
          } else {
            setData([]);
            setError("No data received from server");
          }
        } else {
          setData([]);
          setError(
            resp.data?.message ||
              `Failed to fetch members (status ${resp.status})`,
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error("fetchData error:", err);
        setError("Failed to fetch members");
        setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredMembers = data.filter(
    (member) =>
      member?.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member?.unit_no?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const isMemberSelected = (member) =>
    selectedMembers.some((m) => getMemberKey(m) === getMemberKey(member));

  return (
    <div className="app-container">
      <style>
        {`
        .app-container {
          height: 100vh;
          overflow: hidden;
        }

        .main-layout {
          height: calc(100vh - 64px);
          overflow: hidden;
        }

        .content-area {
          height: 100%;
          overflow-y: auto;
          padding-bottom: 40px;
          background: #f8fafc;
        }

        .compose-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 24px;
        }

        /* Header Section */
        .compose-header {
          margin-bottom: 32px;
        }

        .compose-header-content {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 24px;
        }

        .compose-header-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .compose-header-text h1 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .compose-header-text p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          max-width: 600px;
        }

        /* Compose Form */
        .compose-form-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          overflow: hidden;
        }

        .compose-form-header {
          padding: 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        .compose-form-header h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 20px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .recipients-section {
          margin-bottom: 16px;
        }

        .recipients-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
        }

        /* Member Dropdown */
        .member-dropdown-container {
          position: relative;
          width: 100%;
        }

        .member-dropdown-toggle {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.2s;
        }

        .member-dropdown-toggle:hover {
          border-color: #cbd5e0;
        }

        .member-dropdown-toggle:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .member-toggle-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .member-toggle-text {
          font-size: 14px;
          color: #475569;
        }

        .member-count-badge {
          background: #e0f2fe;
          color: #0369a1;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .member-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          max-height: 300px;
          overflow-y: auto;
          animation: slideDown 0.2s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .member-dropdown-search {
          padding: 12px;
          border-bottom: 1px solid #f1f5f9;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .member-search-input {
          width: 100%;
          padding: 8px 12px 8px 36px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          background: #f8fafc;
        }

        .member-search-icon {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .member-dropdown-actions {
          padding: 8px 12px;
          border-bottom: 1px solid #f1f5f9;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
        }

        .dropdown-action-btn {
          padding: 6px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: white;
          color: #475569;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-action-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e0;
        }

        .member-dropdown-list {
          padding: 8px 0;
        }

        .member-dropdown-item {
          padding: 10px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .member-dropdown-item:hover {
          background: #f8fafc;
        }

        .member-item-checkbox {
          width: 18px;
          height: 18px;
          border: 2px solid #cbd5e1;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .member-item-checkbox.checked {
          background: #0d6efd;
          border-color: #0d6efd;
        }

        .member-item-info {
          flex: 1;
          min-width: 0;
        }

        .member-item-name {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          margin: 0 0 2px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .member-item-unit {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .selected-members-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .member-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #e0f2fe;
          border: 1px solid #7dd3fc;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: #0369a1;
        }

        .chip-remove {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #7dd3fc;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .chip-remove:hover {
          background: #0d6efd;
        }

        /* Form Body */
        .compose-form-body {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
        }

        .required {
          color: #ef4444;
        }

        .form-select {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
        }

        .form-select:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .file-upload-area {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s;
          background: #f8fafc;
        }

        .file-upload-area:hover {
          border-color: #0d6efd;
          background: #f0f9ff;
        }

        .file-upload-area.drag-over {
          border-color: #0d6efd;
          background: #e0f2fe;
        }

        .file-upload-icon {
          color: #64748b;
          margin-bottom: 12px;
        }

        .file-upload-text {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 8px 0;
        }

        .file-upload-hint {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
        }

        .file-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f8fafc;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          margin-top: 12px;
        }

        .file-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .file-name {
          font-weight: 500;
          color: #1e293b;
          font-size: 14px;
        }

        .file-remove {
          background: none;
          border: none;
          cursor: pointer;
          color: #dc2626;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .file-remove:hover {
          background: #fee2e2;
        }

        .form-textarea {
          width: 100%;
          min-height: 200px;
          padding: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          transition: all 0.2s;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .char-count {
          text-align: right;
          font-size: 12px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .compose-form-footer {
          padding: 20px 24px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .recipients-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #64748b;
        }

        .submit-btn {
          padding: 12px 28px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .submit-btn:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Add Occasion Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 32px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .modal-close:hover {
          background: #f8fafc;
        }

        .modal-form-group {
          margin-bottom: 24px;
        }

        .modal-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #475569;
          margin-bottom: 8px;
        }

        .modal-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .modal-input:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 32px;
        }

        .btn-cancel {
          padding: 10px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f8fafc;
          border-color: #cbd5e0;
        }

        .btn-submit {
          padding: 10px 20px;
          background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-submit:hover {
          opacity: 0.9;
        }

        /* Loading States */
        .loading-state {
          padding: 40px;
          text-align: center;
          color: #64748b;
        }

        .spin-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Empty State in Dropdown */
        .dropdown-empty-state {
          padding: 24px;
          text-align: center;
          color: #64748b;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .compose-container {
            padding: 16px;
          }

          .compose-form-footer {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .submit-btn {
            width: 100%;
            justify-content: center;
          }

          .recipients-info {
            justify-content: center;
          }

          .compose-form-header {
            padding: 16px;
          }

          .compose-form-body {
            padding: 16px;
          }
        }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="compose-container">
            {/* Header Section */}
            <div className="compose-header">
              <div className="compose-header-content">
                <div className="compose-header-icon">
                  <Send size={32} />
                </div>
                <div className="compose-header-text">
                  <h1>Compose Notice</h1>
                  <p>
                    Create and send notices to society members. Select
                    recipients, add occasion details, and attach files.
                  </p>
                </div>
              </div>
            </div>

            {/* Compose Form */}
            <div className="compose-form-card">
              <div className="compose-form-header">
                <h2>
                  <FileText size={24} />
                  Compose New Notice
                </h2>

                {/* Member Selection Dropdown */}
                <div className="recipients-section">
                  <label className="recipients-label">
                    <Users size={16} />
                    Select Recipients
                  </label>
                  <div className="member-dropdown-container">
                    <button
                      type="button"
                      className="member-dropdown-toggle"
                      onClick={() =>
                        setIsMemberDropdownOpen(!isMemberDropdownOpen)
                      }
                    >
                      <div className="member-toggle-content">
                        <Users size={18} color="#64748b" />
                        <div className="member-toggle-text">
                          {selectedMembers.length === 0
                            ? "Select members to send notice to..."
                            : selectedMembers.length === data.length
                              ? "All members"
                              : `${selectedMembers.length} member${selectedMembers.length > 1 ? "s" : ""} selected`}
                        </div>
                        {selectedMembers.length > 0 && (
                          <div className="member-count-badge">
                            {selectedMembers.length}
                          </div>
                        )}
                      </div>
                      {isMemberDropdownOpen ? (
                        <ChevronUp size={20} color="#64748b" />
                      ) : (
                        <ChevronDown size={20} color="#64748b" />
                      )}
                    </button>

                    {isMemberDropdownOpen && (
                      <div className="member-dropdown-menu">
                        {/* Search Bar */}
                        <div className="member-dropdown-search">
                          <div style={{ position: "relative" }}>
                            <Search size={16} className="member-search-icon" />
                            <input
                              type="text"
                              placeholder="Search members..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="member-search-input"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="member-dropdown-actions">
                          <button
                            type="button"
                            className="dropdown-action-btn"
                            onClick={toggleSelectAll}
                          >
                            <Check size={14} />
                            {areAllMembersSelected()
                              ? "Deselect All"
                              : "Select All"}
                          </button>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <UserCheck size={14} />
                            {selectedMembers.length} selected
                          </div>
                        </div>

                        {/* Members List */}
                        <div className="member-dropdown-list">
                          {loading ? (
                            <div className="dropdown-empty-state">
                              <Loader size={20} className="spin-animation" />
                              <p style={{ marginTop: "8px", fontSize: "12px" }}>
                                Loading members...
                              </p>
                            </div>
                          ) : error ? (
                            <div className="dropdown-empty-state">
                              <AlertCircle size={20} />
                              <p style={{ marginTop: "8px", fontSize: "12px" }}>
                                Error loading members
                              </p>
                            </div>
                          ) : filteredMembers.length === 0 ? (
                            <div className="dropdown-empty-state">
                              <Users size={20} />
                              <p style={{ marginTop: "8px", fontSize: "12px" }}>
                                No members found
                              </p>
                            </div>
                          ) : (
                            filteredMembers.map((member) => (
                              <div
                                key={getMemberKey(member)}
                                className="member-dropdown-item"
                                onClick={() => toggleMemberSelection(member)}
                              >
                                <div
                                  className={`member-item-checkbox ${isMemberSelected(member) ? "checked" : ""}`}
                                >
                                  {isMemberSelected(member) && (
                                    <Check size={12} color="white" />
                                  )}
                                </div>
                                <div className="member-item-info">
                                  <div className="member-item-name">
                                    {member.member_name || "Unknown"}
                                  </div>
                                  <div className="member-item-unit">
                                    {member.unit_no || "N/A"}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Members Chips */}
                  {selectedMembers.length > 0 && (
                    <div className="selected-members-chips">
                      {selectedMembers.map((member) => (
                        <div key={getMemberKey(member)} className="member-chip">
                          <span>{member.member_name || "Unknown"}</span>
                          <span style={{ opacity: 0.7 }}>
                            ({member.unit_no || "N/A"})
                          </span>
                          <div
                            className="chip-remove"
                            onClick={() => toggleMemberSelection(member)}
                          >
                            <X size={10} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="compose-form-body">
                <form onSubmit={handleSubmit}>
                  {/* Occasion Selection */}
                  <div className="form-group">
                    <label className="form-label">
                      <Tag size={16} />
                      Occasion
                      <span className="required">*</span>
                    </label>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <select
                        required
                        className="form-select"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="" disabled>
                          -- Select Occasion --
                        </option>
                        {occasions.map((oc) => {
                          if (oc && typeof oc === "object") {
                            const val = oc.occasion ?? oc.name ?? String(oc);
                            const label = oc.occasion ?? oc.name ?? String(oc);
                            return (
                              <option key={val} value={val}>
                                {label}
                              </option>
                            );
                          }
                          return (
                            <option key={String(oc)} value={String(oc)}>
                              {String(oc)}
                            </option>
                          );
                        })}
                        <option value="Event">Event</option>
                        <option value="Meeting">Meeting</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowModal(true)}
                        style={{
                          padding: "12px",
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          cursor: "pointer",
                          color: "#0d6efd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title="Add new occasion"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="form-group">
                    <label className="form-label">
                      <Upload size={16} />
                      Attach File
                    </label>
                    <div
                      className="file-upload-area"
                      onClick={() =>
                        document.getElementById("fileInput").click()
                      }
                    >
                      <Upload size={48} className="file-upload-icon" />
                 
                      <p className="file-upload-hint">
                        Supports: only PDF
                      </p>
                      <input
                        id="fileInput"
                        type="file"
                        accept="*/*"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                    </div>
                    {file && (
                      <div className="file-selected">
                        <div className="file-info">
                          <FileText size={20} color="#0d6efd" />
                          <div>
                            <div className="file-name">{file.name}</div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFile(null)}
                          className="file-remove"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Notice Content */}
                  <div className="form-group">
                    <label className="form-label">
                      <FileText size={16} />
                      Notice Content
                      <span className="required">*</span>
                    </label>
                    <textarea
                      value={shortNotice}
                      onChange={(e) => setShortNotice(e.target.value)}
                      className="form-textarea"
                      placeholder="Write your notice here..."
                      required
                    />
                    <div className="char-count">
                      {shortNotice.length} characters
                    </div>
                  </div>
                </form>
              </div>

              <div className="compose-form-footer">
                <div className="recipients-info">
                  <Users size={16} />
                  <span>
                    {selectedMembers.length > 0
                      ? `Sending to ${selectedMembers.length} selected member${selectedMembers.length > 1 ? "s" : ""}`
                      : "Sending to all members"}
                  </span>
                </div>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="submit-btn"
                  disabled={!subject || !shortNotice}
                >
                  <Send size={18} />
                  Send Notice
                </button>
              </div>
            </div>
          </div>

          {/* Add Occasion Modal */}
          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3 className="modal-title">
                    <Tag size={20} />
                    Add New Occasion
                  </h3>
                  <button
                    className="modal-close"
                    onClick={() => setShowModal(false)}
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleModalSubmit}>
                  <div className="modal-form-group">
                    <label className="modal-label">Occasion Name</label>
                    <input
                      type="text"
                      value={modalField}
                      onChange={(e) => setModalField(e.target.value)}
                      className="modal-input"
                      placeholder="Enter occasion name"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-submit">
                      Add Occasion
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <ToastContainer position="bottom-right" autoClose={3000} />
        </main>
      </div>
    </div>
  );
};

export default Compose;
